/**
 * Assessment type classification utilities.
 * Centralises the mapping from assessment type strings to standard categories.
 * @module utils/assessments
 */

import type { Module, ModuleAssessment, Programme, Stage } from "@/types";

import { isFeAwardType } from "./award-types";

/** Formats an assessment's teaching-calendar status for UI and document outputs. */
export function formatAssessmentTiming(
  assessment: Pick<ModuleAssessment, "indicativeWeek" | "outsideTeachingPeriod">,
): string {
  const week =
    typeof assessment.indicativeWeek === "number" ? `Week ${assessment.indicativeWeek}` : "";

  if (assessment.outsideTeachingPeriod === true) {
    return week
      ? `${week} (outside teaching period)`
      : "Outside teaching period, timing not confirmed";
  }

  return week || "Not specified";
}

/** Returns the progression rules attached to an assessment as concise display labels. */
export function assessmentRequirementLabels(
  assessment: Pick<
    ModuleAssessment,
    "isCapstone" | "isHurdle" | "mandatoryPass" | "minimumPassMark"
  >,
): string[] {
  const labels: string[] = [];
  if (assessment.isCapstone === true) {
    labels.push("Capstone");
  }
  if (assessment.isHurdle === true) {
    labels.push("Hurdle");
  }
  if (assessment.mandatoryPass === true) {
    labels.push("Mandatory pass");
  }
  if (assessment.minimumPassMark != null) {
    labels.push(`Minimum pass ${assessment.minimumPassMark}%`);
  }
  return labels;
}

/** Formats an assessment's progression rules for tables and document outputs. */
export function formatAssessmentRequirements(
  assessment: Pick<
    ModuleAssessment,
    "isCapstone" | "isHurdle" | "mandatoryPass" | "minimumPassMark"
  >,
): string {
  return assessmentRequirementLabels(assessment).join(", ") || "Standard module rules";
}

// ============================================================================
// Semester helpers
// ============================================================================

/**
 * Parse a semester field that may contain multiple semesters.
 * "S1" → ["S1"], "S1, S2, S3" → ["S1","S2","S3"], "" → []
 */
export function parseSemesters(semester: string): string[] {
  return semester
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Check whether a module's semester assignment includes a given semester.
 * Handles both "S1" and "S1, S2, S3" formats.
 */
export function moduleInSemester(moduleSemester: string, targetSemester: string): boolean {
  return parseSemesters(moduleSemester).includes(targetSemester);
}

/**
 * Derive the ordered list of distinct semesters from a stage's module list.
 * Returns e.g. ["S1", "S2", "S3"].
 */
export function deriveSemesters(stageModules: { moduleId: string; semester?: string }[]): string[] {
  const semSet = new Set<string>();
  stageModules.forEach((sm) => {
    if (sm.semester) {
      parseSemesters(sm.semester).forEach((s) => {
        semSet.add(s);
      });
    }
  });
  const arr = Array.from(semSet);
  arr.sort((a, b) => {
    const na = parseInt(a.replace(/\D/g, ""), 10) || 0;
    const nb = parseInt(b.replace(/\D/g, ""), 10) || 0;
    return na - nb;
  });
  return arr;
}

// ============================================================================
// Elective helpers
// ============================================================================

/**
 * Return module IDs that should be excluded from credit-weighted calculations
 * because they are alternative elective options. Keeps the first group per
 * definition as representative, excludes the rest.
 */
export function electiveExclusions(
  electiveDefinitions: { groups?: { moduleIds?: string[] }[] }[],
): Set<string> {
  const excluded = new Set<string>();
  electiveDefinitions.forEach((def) => {
    const groups = def.groups ?? [];
    for (let g = 1; g < groups.length; g += 1) {
      (groups[g].moduleIds ?? []).forEach((mid) => {
        excluded.add(mid);
      });
    }
  });
  return excluded;
}

/** Standard assessment categories used in QQI schedules and descriptors. */
export type AssessmentCategory =
  "continuous" | "invigilated" | "proctored" | "project" | "practical" | "workBased";

/** Boolean flags indicating which assessment categories appear. */
export type AssessmentFlags = Record<AssessmentCategory, boolean>;

/** Percentage totals per assessment category. */
export type AssessmentPercentages = Record<AssessmentCategory, number>;

/**
 * Classify an assessment into a standard category.
 *
 * The classifier uses the assessment type as the baseline but will also honor
 * richer metadata when present, such as a work-based delivery mode or an
 * online/proctored integrity flag.
 */
export function classifyAssessmentType(
  assessmentOrType: string | Pick<ModuleAssessment, "type" | "mode" | "integrity">,
): AssessmentCategory {
  const assessment =
    typeof assessmentOrType === "string"
      ? { type: assessmentOrType, mode: undefined, integrity: undefined }
      : assessmentOrType;
  const t = (assessment.type ?? "").toLowerCase();
  const hasCampusHint =
    /\b(campus|onsite|on-site|oncampus|in-person|in person|face-to-face|invigilated)\b/.test(t);
  // AssessmentRow.tsx shows "Online" when a real assessment has no stored
  // mode. That display default applies only when the type itself doesn't
  // already say campus, and the bare-type overload carries no mode at all.
  const modeDefault = typeof assessmentOrType === "string" || hasCampusHint ? "" : "Online";
  const mode = ((assessment.mode ?? modeDefault) as string).toLowerCase();
  const integrity = (assessment.integrity ?? {}) as Record<string, unknown>;
  const isOnlineMode = /\b(online|remote|virtual|digital)\b/.test(mode);
  const isWorkBasedMode = /\b(workbased|work-based|placement|placement-based)\b/.test(mode);
  const isProctored = integrity.proctored === true;
  const hasOnlineHint = /\b(online|remote|virtual|digital|proctored)\b/.test(t);

  if (/(practical|osce|simulation|skills based|skill-based|demonstration)/.test(t)) {
    return "practical";
  }
  if (isWorkBasedMode || /\b(work placement|work-based|work based)\b/.test(t)) {
    return "workBased";
  }
  if (
    (t.includes("exam") || t.includes("examination")) &&
    (isOnlineMode || isProctored || hasOnlineHint)
  ) {
    return "proctored";
  }
  if (
    (t.includes("exam") || t.includes("examination")) &&
    (hasCampusHint ||
      /\b(campus|onsite|on-site|oncampus|in-person|in person|face-to-face|invigilated)\b/.test(
        mode,
      ))
  ) {
    return "invigilated";
  }
  if (t.includes("exam") || t.includes("examination")) {
    return "invigilated";
  }
  if (t.includes("project") || t.includes("dissertation")) {
    return "project";
  }
  if (t.includes("practical") || t.includes("lab")) {
    return "practical";
  }
  if (t.includes("work")) {
    return "workBased";
  }
  return "continuous";
}

/** Empty flags with all categories set to `false`. */
function emptyFlags(): AssessmentFlags {
  return {
    continuous: false,
    invigilated: false,
    proctored: false,
    project: false,
    practical: false,
    workBased: false,
  };
}

/** Empty percentages with all categories set to `0`. */
function emptyPercentages(): AssessmentPercentages {
  return {
    continuous: 0,
    invigilated: 0,
    proctored: 0,
    project: 0,
    practical: 0,
    workBased: 0,
  };
}

/**
 * Determine which assessment categories are present across all modules
 * assigned to a stage.
 */
export function getAssessmentFlags(
  programme: Programme,
  stageModules: Stage["modules"],
): AssessmentFlags {
  const flags = emptyFlags();
  (stageModules ?? []).forEach((sm) => {
    const mod = (programme.modules ?? []).find((m) => m.id === sm.moduleId);
    if (mod?.assessments) {
      mod.assessments.forEach((a: ModuleAssessment) => {
        flags[classifyAssessmentType(a)] = true;
      });
    }
  });
  return flags;
}

/**
 * Sum assessment weightings by category for a single module.
 */
export function getAssessmentPercentages(mod: Module): AssessmentPercentages {
  const pcts = emptyPercentages();
  (mod.assessments ?? []).forEach((a) => {
    pcts[classifyAssessmentType(a)] += a.weighting ?? 0;
  });
  return pcts;
}

// ============================================================================
// Assessment Type Distribution (credit-weighted)
// ============================================================================

/** A single slice in the assessment type distribution. */
export interface AssessmentTypeSlice {
  /** Assessment type label (e.g. "Report/Essay", "Exam (On campus)") */
  type: string;
  /** Credit-weighted value: sum of (module.credits × assessment.weighting / 100) */
  creditWeighted: number;
  /** Percentage of total programme credit-weighted assessment effort */
  pct: number;
}

/**
 * Credit-weighted proportion of each assessment type by stage.
 * For each assessment, contribution = module.credits × (weighting / 100).
 * Percentages are relative to Total stage credits, giving a clean sum to 100%.
 */
export interface StageCreditWeightedRow {
  type: string;
  count: number;
  creditWeighted: number;
  pct: number;
}

/**
 * Build credit-weighted assessment type distribution for a stage.
 * Each assessment's contribution = module.credits × (assessment.weighting / 100).
 * The percentage = contribution / totalStageCredits × 100, summing to ~100%.
 *
 * @param modules - All programme modules (looked up by ID from stage refs)
 * @param stageModuleRefs - The stage's module references
 * @param excludeIds - Optional set of module IDs to exclude (e.g. elective duplicates)
 * @returns Sorted array of rows with type, count, creditWeighted value, and pct
 */
export function buildStageCreditWeightedRows(
  modules: Module[],
  stageModuleRefs: { moduleId: string }[],
  excludeIds?: Set<string>,
): StageCreditWeightedRow[] {
  const modMap = new Map(modules.map((m) => [m.id, m]));
  const typeMap = new Map<string, { count: number; creditWeighted: number }>();
  let totalCreditWeighted = 0;

  (stageModuleRefs ?? []).forEach((ref) => {
    if (excludeIds?.has(ref.moduleId)) {
      return;
    }
    const m = modMap.get(ref.moduleId);
    if (!m) {
      return;
    }
    const credits = Number(m.credits ?? 0);
    (m.assessments ?? []).forEach((a) => {
      const t = a.type || "Unspecified";
      const contribution = credits * (Number(a.weighting ?? 0) / 100);
      const rec = typeMap.get(t) ?? { count: 0, creditWeighted: 0 };
      rec.count += 1;
      rec.creditWeighted += contribution;
      totalCreditWeighted += contribution;
      typeMap.set(t, rec);
    });
  });

  return Array.from(typeMap.entries())
    .map(([type, rec]) => ({
      type,
      count: rec.count,
      creditWeighted: rec.creditWeighted,
      pct: totalCreditWeighted > 0 ? (rec.creditWeighted / totalCreditWeighted) * 100 : 0,
    }))
    .sort((a, b) => b.creditWeighted - a.creditWeighted);
}

/**
 * Build programme-wide assessment type distribution weighted by credits.
 * Each assessment's contribution = module.credits × (assessment.weighting / 100).
 * The result is normalised so percentages sum to 100%.
 * Excludes duplicate elective modules — only counts one representative
 * per elective definition.
 *
 * @param programme - The programme data
 * @returns Sorted array of slices with type, credit-weighted value, and percentage
 */
export function buildAssessmentTypeDistribution(programme: Programme): AssessmentTypeSlice[] {
  const excluded = electiveExclusions(programme.electiveDefinitions ?? []);
  const modules = (programme.modules ?? []).filter((m) => !excluded.has(m.id));
  const typeMap = new Map<string, number>();
  let total = 0;

  modules.forEach((m) => {
    const credits = Number(m.credits ?? 0);
    (m.assessments ?? []).forEach((a) => {
      const t = a.type || "Unspecified";
      const contribution = credits * (Number(a.weighting ?? 0) / 100);
      typeMap.set(t, (typeMap.get(t) ?? 0) + contribution);
      total += contribution;
    });
  });

  return Array.from(typeMap.entries())
    .map(([type, creditWeighted]) => ({
      type,
      creditWeighted,
      pct: total > 0 ? (creditWeighted / total) * 100 : 0,
    }))
    .sort((a, b) => b.creditWeighted - a.creditWeighted);
}

// ============================================================================
// Workload vs ECTS Alignment
// ============================================================================

/** Data for a single module in the workload vs ECTS chart. */
export interface WorkloadEctsRow {
  moduleId: string;
  label: string;
  credits: number;
  expectedHours: number;
  actualHours: number;
  deviation: number;
}

function getHoursPerCredit(programme: Programme): number {
  return isFeAwardType(programme.awardType ?? "") ? 10 : 25;
}

/**
 * Build workload alignment data for each module.
 * HE default: 1 credit = 25 hours. FE default: 1 credit = 10 hours.
 * Sums all effort hour fields for the first version-modality key found.
 * Excludes duplicate elective modules.
 *
 * @param programme - The programme data
 * @returns Array of rows per module with expected vs actual hours
 */
export function buildWorkloadEctsData(programme: Programme): WorkloadEctsRow[] {
  const excluded = electiveExclusions(programme.electiveDefinitions ?? []);
  const modules = (programme.modules ?? []).filter((m) => !excluded.has(m.id));
  const hoursPerCredit = getHoursPerCredit(programme);

  return modules.map((m) => {
    const credits = Number(m.credits ?? 0);
    const expectedHours = credits * hoursPerCredit;

    // Sum effort hours from the first version-modality key available
    let actualHours = 0;
    const effortMap: Record<string, unknown> = m.effortHours ?? {};
    const keys = Object.keys(effortMap);
    if (keys.length > 0) {
      const e = (effortMap[keys[0]] ?? {}) as Record<string, unknown>;
      actualHours =
        Number(e.classroomHours ?? 0) +
        Number(e.mentoringHours ?? 0) +
        Number(e.otherContactHours ?? 0) +
        Number(e.directedElearningHours ?? 0) +
        Number(e.independentLearningHours ?? 0) +
        Number(e.otherHours ?? 0) +
        Number(e.workBasedHours ?? 0);
    }

    const label = m.code && m.code.trim() ? m.code.trim() : m.title;

    return {
      moduleId: m.id,
      label,
      credits,
      expectedHours,
      actualHours,
      deviation: actualHours - expectedHours,
    };
  });
}
